// store/financialThunks.ts
// Profile load/save. fetch reconciles the server document against the local
// cache (last-write-wins both ways); save writes the active-session key.

import { createAsyncThunk } from '@reduxjs/toolkit';
import { activeFinancialKey, isAnonymousSession } from '../lib/sessionMode';
import { syncGetProfile, syncPutProfile } from '../lib/syncApi';
import { ANON_ZERO_STATE, DEFAULT_STATE } from './financialDefaults';
import { loadFromStorage } from './financialMigration';
import type { FinancialState } from './financialTypes';

export const fetchFinancialProfile = createAsyncThunk(
	'financial/fetchProfile',
	async (_, { rejectWithValue }) => {
		try {
			if (isAnonymousSession()) {
				// Anonymous world: its own key, empty slate when new.
				return loadFromStorage(activeFinancialKey()) ?? ANON_ZERO_STATE;
			}

			// Authed: server document ↔ local cache, LWW both ways.
			const local = loadFromStorage();
			let server: Awaited<ReturnType<typeof syncGetProfile>> = null;
			let serverReachable = true;
			try {
				server = await syncGetProfile();
			} catch {
				serverReachable = false; // offline / API down → local world
			}

			if (server !== null) {
				const localTs = local?._lastUpdated ?? 0;
				if (server.clientUpdatedAt >= localTs) {
					// Server is as new or newer — adopt it. The fulfilled merge
					// runs the usual client migrations over it.
					return server.data as Partial<FinancialState>;
				}
				// Local is NEWER (offline edits): push up, adopt the winner.
				try {
					const { _syncStatus: _t, ...doc } = local as FinancialState;
					const winner = await syncPutProfile(doc, localTs);
					return winner.data as Partial<FinancialState>;
				} catch {
					return local as Partial<FinancialState>;
				}
			}

			// No server document yet.
			if (local) {
				// First login on a device with data (including the signup
				// carry-over's adopted numbers) seeds the server.
				if (serverReachable) {
					try {
						const { _syncStatus: _t, ...doc } =
							local as FinancialState;
						await syncPutProfile(
							doc,
							local._lastUpdated ?? Date.now(),
						);
					} catch {
						/* kept locally; middleware pushes on next edit */
					}
				}
				return local;
			}
			// Fresh account, fresh device: the starter profile stays a LOCAL
			// first-run experience — never seeded to the server.
			return DEFAULT_STATE;
		} catch (err) {
			console.log('Failed to load financial profile', err);
			return rejectWithValue('Failed to load financial profile');
		}
	},
);

export const saveFinancialProfile = createAsyncThunk(
	'financial/saveProfile',
	async (data: Partial<FinancialState>, { rejectWithValue }) => {
		try {
			const key = activeFinancialKey();
			const current =
				loadFromStorage(key) ??
				(isAnonymousSession() ? ANON_ZERO_STATE : DEFAULT_STATE);
			const next = { ...current, ...data, _lastUpdated: Date.now() };
			localStorage.setItem(key, JSON.stringify(next));
			return next;
		} catch (err) {
			console.log('Failed to save financial profile', err);
			return rejectWithValue('Failed to save financial profile');
		}
	},
);
